import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { z } from 'zod'
import { Code2, FileText, History, LayoutGrid } from 'lucide-react'
import { toast } from 'sonner'
import { AppHeader } from '#/components/layout/app-header'
import { ChoiceCard } from '#/components/plan/choice-card'
import { TimelineMini } from '#/components/plan/timeline-mini'
import { Stepper } from '#/components/plan/stepper'
import { IdeaForm } from '#/components/plan/idea-form'
import { PlanDrawer } from '#/components/plan/plan-drawer'
import { PromptBlock } from '#/components/common/prompt-block'
import { api, useWorkspacePlans } from '#/lib/api'
import { authClient } from '#/lib/auth-client'
import { m } from '#/paraglide/messages'

export const Route = createFileRoute('/plan')({
  // Parity P5: `kit`/`playbook` come from /playbooks → pre-select that starter kit on the idea form
  validateSearch: z.object({ flow: z.enum(['new', 'existing']).optional(), workspace: z.string().optional(), kit: z.string().max(60).optional(), playbook: z.string().max(60).optional() }),
  component: PlanPage,
})

function PlanPage() {
  const { flow, workspace, kit, playbook } = Route.useSearch()
  const navigate = useNavigate()
  const { data: session, isPending } = authClient.useSession()
  useEffect(() => {
    if (flow && !isPending && !session?.user) navigate({ to: '/sign-in', search: { redirect: `/plan?flow=${flow}${kit ? `&kit=${encodeURIComponent(kit)}` : ''}${playbook ? `&playbook=${encodeURIComponent(playbook)}` : ''}` } })
  }, [flow, isPending, session, navigate, kit, playbook])
  if (flow === 'new') {
    return (
      <>
        <AppHeader showStats={false} center={<Stepper steps={[m.wizard_step_idea(), m.wizard_step_docs(), m.wizard_step_export()]} current={0} />} />
        <main className="flex-1 overflow-y-auto px-6 pb-12 pt-8 sm:px-8 sm:pt-12">
          <IdeaForm initialKitId={kit ?? null} />
        </main>
      </>
    )
  }
  if (flow === 'existing') return <ExistingFlow workspaceId={workspace} />
  return <Chooser />
}

function Chooser() {
  const [drawer, setDrawer] = useState(false)
  return (
    <>
      <AppHeader showStats={false} />
      <main className="flex flex-1 flex-col items-center justify-center overflow-y-auto px-6 py-10">
        <div className="w-full max-w-4xl space-y-6">
          <h1 className="text-center text-3xl font-bold sm:text-4xl">{m.plan_choose_title()}</h1>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <ChoiceCard
              to="/plan"
              search={{ flow: 'new' }}
              icon={<FileText className="size-5" />}
              tone="green"
              title={m.plan_choose_new_title()}
              description={m.plan_choose_new_desc()}
              footer={<TimelineMini steps={[m.plan_choose_new_t1(), m.plan_choose_new_t2(), m.plan_choose_new_t3()]} />}
            />
            <ChoiceCard
              to="/plan"
              search={{ flow: 'existing' }}
              icon={<Code2 className="size-5" />}
              tone="amber"
              title={m.plan_choose_existing_title()}
              description={m.plan_choose_existing_desc()}
              footer={<TimelineMini steps={[m.plan_choose_existing_t1(), m.plan_choose_existing_t2(), m.plan_choose_existing_t3()]} />}
            />
            <ChoiceCard
              to="/room"
              icon={<LayoutGrid className="size-5" />}
              tone="violet"
              title={m.dr_plan_choose_title()}
              description={m.dr_plan_choose_desc()}
              footer={<TimelineMini steps={[m.dr_plan_choose_t1(), m.dr_plan_choose_t2(), m.dr_plan_choose_t3()]} />}
            />
          </div>
          <div className="text-center">
            <button className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" onClick={() => setDrawer(true)}>
              <History className="size-4" /> {m.plan_choose_previous()}
            </button>
          </div>
        </div>
      </main>
      <PlanDrawer open={drawer} onOpenChange={setDrawer} />
    </>
  )
}

function ExistingFlow({ workspaceId }: { workspaceId?: string }) {
  const { data: session } = authClient.useSession()
  const [boot, setBoot] = useState<{ workspaceId: string; prompt: string } | null>(null)
  const navigate = useNavigate()
  useEffect(() => {
    if (!session?.user || boot) return
    api<{ workspaceId: string; prompt: string }>('/api/workspaces/bootstrap', { method: 'POST', json: workspaceId ? { workspaceId } : {} })
      .then(setBoot)
      .catch(() => toast.error(m.common_error_generic()))
  }, [session, boot, workspaceId])
  const { data: ws } = useWorkspacePlans(boot?.workspaceId ?? '00000000-0000-0000-0000-000000000000')
  const status = ws?.workspace.codebaseStatus ?? 'none'
  const step = status === 'indexed' ? 2 : status === 'none' ? 0 : 1
  useEffect(() => {
    if (status === 'indexed' && boot) navigate({ to: '/workspace/$workspaceId/$view', params: { workspaceId: boot.workspaceId, view: 'wiki' }, search: {} })
  }, [status, boot, navigate])
  return (
    <>
      <AppHeader showStats={false} center={<Stepper steps={[m.wizard_step_copy(), m.wizard_step_analyse(), m.wizard_step_saved()]} current={step} />} />
      <main className="flex-1 overflow-y-auto px-6 pb-12 pt-8 sm:px-8 sm:pt-12">
        <div className="mx-auto w-full max-w-2xl space-y-8">
          <div className="space-y-2">
            <h1 className="flex items-center gap-3 text-3xl font-bold">
              <span className="rounded-lg bg-muted px-2 py-0.5 font-mono text-lg">{'</>'}</span> {m.existing_title()}
            </h1>
            <p className="text-muted-foreground">
              {m.existing_subtitle_prefix()}
              <Link to="/plan" search={{ flow: 'new' }} className="rounded-md border bg-muted px-1.5 py-0.5 text-xs text-foreground hover:border-primary">
                📄 {m.existing_subtitle_link()}
              </Link>
            </p>
          </div>
          <section className="space-y-3">
            <h2 className="font-heading text-lg font-semibold">1. {m.existing_step1()}</h2>
            {boot ? <PromptBlock text={boot.prompt} warning={m.impl_bootstrap_warning()} /> : <div className="h-40 animate-pulse rounded-lg bg-muted" />}
          </section>
          <section className="space-y-2">
            <h2 className="font-heading text-lg font-semibold">2. {m.existing_step2()}</h2>
            <p className="text-sm text-muted-foreground">{m.existing_step2_desc()}</p>
            <p className="text-xs text-muted-foreground">{m.existing_agents()}</p>
          </section>
          <section className="rounded-xl border bg-card p-4 text-sm">
            <span className="mr-2 inline-block size-2 animate-pulse rounded-full bg-status-progress" />
            {status === 'none' ? m.existing_waiting() : status === 'indexed' ? m.existing_saved() : m.existing_analysing()}
          </section>
        </div>
      </main>
    </>
  )
}
